import { LoadingButton } from "@mui/lab";
import { Box, Button, Grid, Skeleton, Typography } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { FairPaySurveyResponseContext } from "contexts/FairPaySurveyResponse";
import { useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { CustomEvent } from "schemas/functions";

import {
  ErrorField,
  FairPayOnboardingPage,
  FairPayOnboardingPagesOrder,
  RequiredFieldLabel,
  RequiredFieldsForFairPaySurveyResponse,
} from "features/FairPaySurvey/schemas";
import { fetcher } from "utils/api";
import { trackEvent } from "utils/tracking";

import FairPayAboutYou from "./pages/FairPayAboutYou";
import FairPayConnectGmail from "./pages/FairPayConnectGmail";
import FairPayDealsEarning from "./pages/FairPayDealsEarnings";
import FairPaySocial from "./pages/FairPaySocial";
import styles from "./styles";

const SurveyOnboardingForm = () => {
  const { surveyUuid } = useParams();
  const { fairPaySurveyResponse, setFairPaySurveyResponse } = useContext(
    FairPaySurveyResponseContext,
  );
  const { setAlert, setErrorAlert } = useContext(AlertContext);
  const ref = useRef<HTMLDivElement>(null);

  const [error, setError] = useState<ErrorField | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<FairPayOnboardingPage>(
    FairPayOnboardingPage.socials,
  );
  const currentPageIndex = FairPayOnboardingPagesOrder.findIndex(
    (x) => x === currentPage,
  );
  const nextPageName =
    currentPageIndex !== -1 &&
    currentPageIndex + 1 < FairPayOnboardingPagesOrder?.length
      ? FairPayOnboardingPagesOrder[currentPageIndex + 1]
      : "";
  const previousPageName =
    currentPageIndex > 0
      ? FairPayOnboardingPagesOrder[currentPageIndex - 1]
      : "";

  const fetchSurvey = async () => {
    if (!surveyUuid) {
      return null;
    }
    setLoading(true);
    try {
      const { surveyResponse } = await fetcher(
        `/api/organization/fair-pay-survey-responses/${surveyUuid}`,
        "GET",
      );
      setFairPaySurveyResponse(surveyResponse);
    } catch {
      return null;
    } finally {
      setLoading(false);
    }
  };

  const validateFields = () => {
    if (error) {
      setAlert(error.message, "warning");
      return false;
    }

    const requiredFields = RequiredFieldsForFairPaySurveyResponse[currentPage];
    if (!requiredFields || requiredFields.length === 0) {
      return true;
    }
    for (const field of requiredFields) {
      const value = (fairPaySurveyResponse as any)?.[field];
      if (
        value === null ||
        value === undefined ||
        (typeof value === "string" && value.trim() === "")
      ) {
        // @ts-ignore
        setAlert(`Please fill in ${RequiredFieldLabel?.[field]}`, "warning");
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e: CustomEvent) => {
    e.preventDefault();
    if (!fairPaySurveyResponse || !surveyUuid) {
      return;
    }
    const canSubmit = validateFields();
    if (!canSubmit) {
      return;
    }
    trackEvent("Fair Pay - Onboarding Next Button Clicked", {
      "Survey UUID": surveyUuid,
      Step: currentPage,
    });
    setSubmitLoading(true);
    try {
      const { surveyResponse } = await fetcher(
        `/api/organization/fair-pay-survey-responses/${surveyUuid}`,
        "PUT",
        {},
        fairPaySurveyResponse,
      );
      setFairPaySurveyResponse(surveyResponse);
      if (nextPageName) {
        setCurrentPage(nextPageName);
      }
      scrollToTop();
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleBack = () => {
    trackEvent("Fair Pay - Onboarding Back Button Clicked", {
      "Survey UUID": surveyUuid,
      Step: currentPage,
    });
    if (previousPageName) {
      setCurrentPage(previousPageName);
    }
    scrollToTop();
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  useEffect(() => {
    if (surveyUuid) {
      fetchSurvey();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyUuid]);

  return (
    <Grid container justifyContent="center" sx={styles.root}>
      <Box sx={{ maxWidth: 500 }}>
        <Typography variant="h2" sx={styles.header}>
          Quick 4-Step Setup
        </Typography>
        <Typography sx={styles.subtitle}>
          Your data is stays private — we never sell or share it, only
          anonymized insights.
        </Typography>

        <Box sx={styles.formContainer}>
          {loading ? (
            <Box>
              <Skeleton
                animation="wave"
                width="100%"
                height="100px"
                sx={{ mb: 1 }}
              />
              <Skeleton
                animation="wave"
                width="100%"
                height="400px"
                sx={{ mb: 2, mt: -12 }}
              />
              <Skeleton
                animation="wave"
                width="100%"
                height="200px"
                sx={{ mb: 2, mt: -14 }}
              />
              <Skeleton
                animation="wave"
                width="100%"
                height="200px"
                sx={{ mb: 2, mt: -10 }}
              />
            </Box>
          ) : (
            <form onSubmit={handleSubmit}>
              <>
                <Grid container justifyContent={"center"}>
                  <Box sx={styles.chip} ref={ref}>
                    Step {currentPageIndex + 1}/
                    {FairPayOnboardingPagesOrder?.length}
                  </Box>
                </Grid>
                {currentPage === FairPayOnboardingPage.socials && (
                  <FairPaySocial error={error} setError={setError} />
                )}
                {currentPage === FairPayOnboardingPage.about_you && (
                  <FairPayAboutYou />
                )}
                {currentPage === FairPayOnboardingPage.deals && (
                  <FairPayDealsEarning error={error} setError={setError} />
                )}
                {currentPage === FairPayOnboardingPage.connect && (
                  <FairPayConnectGmail />
                )}
                <Grid container justifyContent={"center"}>
                  {nextPageName && (
                    <LoadingButton
                      fullWidth
                      disableElevation
                      variant="contained"
                      sx={styles.nextPage}
                      loading={submitLoading}
                      disabled={fairPaySurveyResponse === null}
                      type="submit"
                    >
                      Next: {nextPageName}
                    </LoadingButton>
                  )}
                </Grid>
              </>
            </form>
          )}
        </Box>

        <Grid container justifyContent={"center"}>
          {previousPageName && (
            <Button sx={styles.backButton} onClick={handleBack}>
              <Box
                sx={{ mr: 1 }}
                component="i"
                className="fa-regular fa-chevron-left"
              />{" "}
              Back to {previousPageName}
            </Button>
          )}
        </Grid>
      </Box>
    </Grid>
  );
};

export default SurveyOnboardingForm;
